import { Ref, unref } from '@vue/composition-api';
import { ErrorObject, Validation, ValidationRuleCollection } from '@vuelidate/core';
import * as validators from '@vuelidate/validators';
import { getUniqueArray, getValueByPath } from '@berlin-front/common/utils';
import validKanaChars from './validKanaChars';
import validZenkakuChar from './validZenkakuChars';

// 携帯番号
export const REG_PHONE_ONLY_NUMBER_STYLE = /^0[6-9]0\d{8}$/;
export const REG_PHONE_ONLY_NUMBER_STYLE_ERR_MSG = 'ハイフンなしの携帯番号を入力してください。';

/**
 * Rubyで定義されているemailフォーマットの正規表現から、ドメインパートのIPアドレスを許容するルールを除外したもの。
 *
 * RFCより弱い正規表現となっているのはドコモのメアドなど、古くてRFCに準拠してないメールアドレスがあるためという話。
 *
 * また、IPアドレスの除外は
 *
 * - そもそもIPアドレスを指定したメアドはバックエンド側でシステム例外が発生するため登録できない
 * - IPアドレスを指定したメールアドレスはIPが変動した場合、誰に送信されてしまうかわからず、誤配による情報漏洩の危険があり、コンプライアンス的にNG
 *
 * という理由から行っている。
 *
 * ドメインパートについてはxn--で始まる国際化ドメインを入力される想定が一応行われているが、
 * 国際化ドメインのasciiコードへのエンコードは、コード上実装されていない。
 * 今後、それについてのやるやらの話が出たりする場合、
 *
 * - バリデーションルールの変更
 * - fufururu-apiから取得したemailアドレスについて、xn--から始まるドメインを日本語ドメイン形式に逆変換表示する
 *
 * といった事について考慮何かしら考慮が必要になるかもしれない。
 * @see https://punycode.jp/
 *
 * 細かいこと言うと、正規表現パターンでバリデートするのはあまり褒められない。
 * ローカルパート、ドメイン、サブドメインを分割してから個別に評価すれば、わかりやすく、正確性の高いバリデーションが可能であるため。
 * 全体的にバリデーションの質が低い気がする。
 *
 * 参考: originalのRuby側で定義されているメールアドレスチェック用正規表現を、js用に移植したものはこうなる。
 * /^[-a-zA-Z0-9_!#$%&*+/=?^`{|}~.]+@(([-a-zA-Z0-9]+(\.[-a-zA-Z0-9]+)*\.(([a-zA-Z]{2,})|(xn--[0-9a-zA-Z]+)))|(\[(([1-9]?[0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([1-9]?[0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\]))$/i;
 */
export const REG_EMAIL_STYLE =
  /^[-a-zA-Z0-9_!#$%&*+/=?^`{|}~.]+@[-a-zA-Z0-9]+(\.[-a-zA-Z0-9]+)*\.(([a-zA-Z]{2,})|(xn--[0-9a-zA-Z]+))$/i;

export const REG_EMAIL_STYLE_ERR_MSG = 'メールアドレス形式で入力してください。';

export const REG_CORP_STYLE =
  /^(?!.*(株式会社|（株）|有限会社|（有）|合名会社|合同会社|（合）|合資会社|（資）|財団法人|（財）|一般社団法人|（一財）|一般財団法人|（公財）|社団法人|（社）|医療法人|（医）|宗教法人|（宗）|学校法人|（学）|社会福祉法人|（福）|特定非営利活動法人|（特非）|独立行政法人|（独）)).*$/;

export const REG_PHONE_NUMBER_STYLE = /^[0][6-9][0]\d{8}$/;
export const REG_PHONE_NUMBER_STYLE_ERR_MSG = '携帯電話番号を入力してください。';
export const REG_ZIP_STYLE = /^\d{7}$/;
export const REG_ZIP_STYLE_ERR_MSG = '郵便番号（７桁）の形式が不正です。';
export const REG_DATE_OF_BIRTH = /^([0][1-9]|[1-9]|[12][0-9]|3[01])$/;
export const REG_DATE_OF_BIRTH_ERR_MSG = '1から31までの日を入力しください。';
export const REG_YEAR_OF_BIRTH = /^([1][9]|[2][0])[0-9][0-9]$/;
export const REG_YEAR_OF_BIRTH_ERR_MSG = '1900年以前、未来西暦では指定できません。';
/**
 * 8桁以上30桁以下の英数半角
 */
export const REG_PASSWORD = /^(?=.*\d+.*)(?=.*[a-zA-Z]+.*)[0-9a-zA-Z]{8,30}$/;

// TODO: メッセージの差し替えも必要になってくると思うので、要対応。atoneのSMDL側を改修する方法もあるが、NP後払い側はそもそもメッセージ定義がないので、いずれにせよ今後何かしらつくる必要あり。
export type AtoneErrorObject = { code: string; messages: string[]; params?: string[] };
export type AtoneErrors = AtoneErrorObject[];
export const getAtoneErrorMessage = (
  errors: Ref<AtoneErrors> | AtoneErrors,
  errorCodeList: string[],
  messages: string[] = [],
) => {
  if (!unref(errors)) return [];
  const errCodes: string[] = getUniqueArray(errorCodeList);
  let errorMessages: string[] = [];
  errCodes.forEach((errCode) => {
    const msgs = unref(errors)
      .filter((err) => err.code === errCode)
      .flatMap((err) => err.messages);
    if (msgs.length > 0) errorMessages = errorMessages.concat(msgs);
  });
  return messages.concat(getUniqueArray(errorMessages));
};

type Join<T extends string, U extends string> = T extends '' ? U : `${T}.${U}`;
type GetAllKeyOfValidationArgs<T, P extends string = ''> = keyof T extends infer U
  ? U extends keyof T & string
    ? T[U] extends infer V
      ? V extends ValidationRuleCollection<unknown>
        ? Join<P, U>
        : P extends ''
          ? GetAllKeyOfValidationArgs<V, U>
          : Join<P, U>
      : never
    : never
  : never;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RefValidationToFlatErrorObjectKeys<T extends Ref<Validation<any>>> =
  T extends Ref<Validation<infer U>> ? GetAllKeyOfValidationArgs<U, ''> : never;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getErrors =
  <
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    T extends Ref<Validation<any>>,
  >(
    v$: T,
  ) =>
  (path: RefValidationToFlatErrorObjectKeys<T>) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    const v = getValueByPath(
      v$ as Record<string, unknown>,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      `value.${path as string}`,
      { $pending: false, $error: false, $silentError: [] as ErrorObject[], $errors: [] as ErrorObject[] },
    ) as {
      $pending: boolean;
      $error: boolean;
      $silentError: ErrorObject[];
      $errors: ErrorObject[];
    };
    return {
      ...v,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      $messages: v.$errors.map((err) => unref(err.$message)),
    };
  };

/**
 * UTF16の可変長キャラクターを考慮したうえで、文字列を1文字ずつの配列に分解します。
 *
 * 当プロジェクトでは、文字種バリデーションをホワイトリストによる比較で行うことから、正しい文字区切りを認識したうえで
 * 文字の配列として文字列を扱う必要があります。
 *
 * jsの文字は内部的にはUTF-16として保持されていますが、UTF-16文字には2バイト文字と4バイト文字があり、可変長です。
 * しかし、jsにはこの文字区切りを正確に認識するためのAPIが用意されていないため（最新のAPIなら別だが）、
 * 単純に1文字ずつに分解しようとすると、すべて2バイトとして処理されてしまいます。
 * この関数ではビット列を検証することで、区切り位置を正しく認識したうえで、1文字ずつに分解することができます。
 *
 * ただし合字（ligature）は1文字として認識されない。
 * （例えば `'👨‍👨‍👦‍👦'` は`'👨👨👦👦'`などを含んだ合字として表されている）
 *
 * @param text
 * @returns
 */
const splitTextToUTF16Char = (text: string) => {
  /** 引数の1文字列を1文字ずつに分割した結果を格納する配列。 */
  const chars: string[] = [];
  // UTF-16は2バイト文字と4バイト文字の可変長文字コード。
  // 一旦splitするが、この場合、4バイト文字は考慮されず、2バイト毎に強制分割される。
  // この後、必要に応じて、4バイト文字の上位桁と下位桁はマージする。
  text.split('').forEach((ch) => {
    const lastIndex = chars.length - 1;
    const lastchar = chars[lastIndex];
    const currentCharCode = ch.charCodeAt(0);
    if (
      currentCharCode >= 0b1101_1100_0000_0000 &&
      currentCharCode < 0b1110_0000_0000_0000 &&
      typeof lastchar === 'string'
    ) {
      // 4バイト文字の下位2バイトの場合の処理は、直前の文字にマージする事。
      // - 下位2バイトなら文字コードのビットパターンは1101_11xx_xxxx_xxxx
      // - 直前の文字が上位2バイトなら `'0b1101_10'` で開始するが、frontendではそこまで検証する意味はない。
      chars[lastIndex] = lastchar + ch;
    } else {
      // 4バイト文字の下位2バイトに該当しないなら新しい文字として配列の末尾に積む。
      chars.push(ch);
    }
  });
  return chars;
};
/**
 * 引数の文字列がシステム上許容されるかな文字のみで構成されるかどうかをチェックする。
 *
 * @param value
 * @returns
 */
export const isValidKanaString = (value: string): boolean =>
  splitTextToUTF16Char(value).every((char) => validKanaChars.includes(char));

/**
 * 引数の文字列がシステム上許容される全角文字のみで構成されるかどうかをチェックする。
 *
 * @param value
 * @returns
 */
export const isValidZenkakuString = (value: string): boolean =>
  splitTextToUTF16Char(value).every((char) => validZenkakuChar.includes(char));

// 引数なしのvalidator。
/**
 * 必須バリデータ。
 */
export const required = validators.helpers.withMessage(
  // カスタムメッセージ
  () => '入力してください。',
  // validatorの実装。
  validators.required,
);

/**
 * 番地の必須メッセージのextension
 */
export const EXTENSION_MESSAGE_ADDRESS2_REQUIRED = '番地がない方は「番地なし」と入力してください。';

/**
 * 必須バリデータ。
 */
export const targetRequired = (target: string, extensionMessage?: string) =>
  validators.helpers.withMessage(
    // カスタムメッセージ
    () => `${target}は必須です。${extensionMessage ?? ''}`,
    // validatorの実装。
    validators.required,
  );

/**
 * 必須バリデータ（メッセージなし）。
 */
export const requiredQuiet = validators.helpers.withMessage(
  // 半角スペースを設定（発火のためにメッセージを渡す必要あり）
  () => ' ',
  // validatorの実装。
  validators.required,
);

// 引数ありのバリデータ。
// 引数はRefと非Refのものを取れるように作る。
// Refならば相関バリデーション可能と思う。
// 引数をとって、引数なしバリデータを返せば良い。

/**
 * 最大数値バリデータ。
 * @param max
 * @returns
 */
export const maxValue = (max: Ref<number> | number) =>
  // 中身はwithMessageを使用した引数なしバリデータを返せば良い。
  validators.helpers.withMessage(
    //
    () => `${unref(max)}以下で入力してください。`,
    validators.maxValue(max),
  );

/**
 * 最小数値バリデータ。
 * @param min
 * @returns
 */
export const minValue = (min: Ref<number> | number) =>
  validators.helpers.withMessage(
    //
    () => `${unref(min)}以上で入力してください。`,
    validators.minValue(min),
  );

/**
 * 最大文字列長バリデータ。
 * @param max
 * @returns
 */
export const maxLength = (max: Ref<number> | number) =>
  validators.helpers.withMessage(
    //
    () => `${unref(max)}文字以下で入力してください。`,
    (input: string) => (typeof input === 'string' ? splitTextToUTF16Char(input).length <= unref(max) : true),
  );

/**
 * 最小文字列長バリデータ。
 * @param min
 * @returns
 */
export const minLength = (min: Ref<number> | number) =>
  validators.helpers.withMessage(
    //
    () => `${unref(min)}文字以上で入力してください。`,
    (input: string) => (typeof input === 'string' ? splitTextToUTF16Char(input).length >= unref(min) : true),
  );

/**
 * 全角姓名バリデータ
 * @param param
 * @returns
 */
export const zenkaku = (param: string) =>
  validators.helpers.withMessage(
    () => `${param}は全角で入力してください。`,
    (input: string) => isValidZenkakuString(input),
  );

/**
 * かな姓名バリデータ
 * @param param
 * @returns
 */
export const kana = (param: string) =>
  validators.helpers.withMessage(
    () => `${param}は全角のひらがな・カタカナ・ー（伸ばし棒）以外使用できません。`,
    (input: string) => isValidKanaString(input),
  );

/**
 * 非法人バリデータ
 * @param param
 * @returns
 */
export const corpRegex = () =>
  validators.helpers.withMessage(
    () => `法人利用はできません。`,
    (input: string) => REG_CORP_STYLE.test(input),
  );

/**
 * 郵便番号バリデータ
 * @param
 * @returns
 */
export const zipRegex = () =>
  validators.helpers.withMessage(
    () => '郵便番号（７桁）の形式が不正です。',
    (input: string) => REG_ZIP_STYLE.test(input),
  );

/**
 * 正規表現バリデータ。
 * @param regExpression
 * @param regErrMessage
 * @returns
 */
export const regexp = (regExpression: Ref<RegExp> | RegExp, regErrMessage?: string) =>
  validators.helpers.withMessage(
    () => regErrMessage || '不正な形式です。',
    (input: string) => unref(regExpression).test(input),
  );

/**
 * 市区町村・番地・建物名に跨る最大長チェック
 *
 * @param addr アドレスを総合したもの。
 * @returns
 */
export const fullAddress = (addr: Ref<string>) =>
  validators.helpers.withMessage(
    //
    () => `市区町村・番地と建物名は合計50文字以内で入力してください`,
    () => splitTextToUTF16Char(unref(addr)).length <= 50,
  );

/**
 * email正規表現チェック
 */
export const email = validators.helpers.withMessage(
  () => REG_EMAIL_STYLE_ERR_MSG,
  // 旧モーダルにおいてemail形式バリデーションはemptyでなく、正規表現を満たしていること、となっている。
  (value: string) => value.length === 0 || REG_EMAIL_STYLE.test(value),
);

/**
 * 入力が期待値配列のどれかと一致することをチェックするバリデータ。
 *
 * @param values 期待値配列
 * @param errMessage エラーメッセージ
 * @returns
 */
export const includes = (values: readonly string[] | Ref<string[]>, errMessage?: string | Ref<string>) =>
  validators.helpers.withMessage(
    () => unref(errMessage) || '不正な形式です。',
    (input: string) => unref(values).includes(input),
  );

/**
 * 2, 4, 6, 9, 11月の日バリデーション。
 * 日付入力欄に対してセットする。
 *
 * 日に対するバリデーションは1-31かどうかのチェックが別にある。
 * 2, 4, 6, 9, 11月以外の月もこのバリデーション設定してはいるが、全て1-31日チェックでエラーに引っかかる。
 * 2月は28~29で、4, 6, 9, 11月は30が月末のため1-31のチェックだけでは抜けてしまう。
 * 正確な日付チェックは年、月、日すべてが埋まってないと行えない（2月に関してはうるう年もある）。
 * 年や月が未入力の場合は1~31日チェックまでしか行えないため、ふるい1-31日チェックは残しておく必要がある。
 *
 * @param year 相関データとして年を与える必要がある。正しい年が与えられない場合はバリデーションOKとする。
 * @param month 相関データとして月を与える必要がある。正しい月が与えられない場合はバリデーションOKとする。
 * @param errMessage
 * @returns
 */
export const validDay = (year: string | Ref<string>, month: string | Ref<string>, errMessage?: string | Ref<string>) =>
  validators.helpers.withMessage(
    () => unref(errMessage) || '不正な形式です。',
    (input: string) => {
      const birthday = `${unref(year)}-${unref(month)}-${input}`;
      // フォーマットが異なる場合は日、月、年の個別バリデーションで対応するためスルーする。
      if (!/^\d{4}-\d{2}-\d{2}$/.test(birthday)) return true;
      try {
        return (new Date(`${birthday}T00:00:00.000Z`)?.toISOString() ?? 'T').split('T')[0] === birthday;
      } catch {
        return false;
      }
    },
  );

export type ValidatorRule = Record<string, () => boolean>;

export type ValidatorTypeToGetError<VT extends Parameters<typeof getErrors>[0]> = ReturnType<typeof getErrors<VT>>;

export type Rule<InputType> = {
  [Property in keyof InputType as InputType[Property] extends undefined ? never : Property]: ValidatorRule;
};
